import * as THREE from 'three';
import { registerShot, look } from '../core/Shots.js';
import { makeRng, clamp } from '../core/Rand.js';
import { GunMaterialLibrary } from './GunMaterials.js';
import { DEFS, buildWeapon } from './WeaponDefs.js';
import { Viewmodel, WeaponInstance } from './Viewmodel.js';
import { RecoilState, SpreadState, recoilImpulse } from './Recoil.js';
import { Ballistics } from './Ballistics.js';
import {
  reloadSequence, pumpSequence, shellLoadSequence, meleeSequence,
  inspectSequence, drawSequence, holsterSequence,
} from './Sequences.js';
import { clamp01, smoothstep, lerp } from './Anim.js';

/**
 * Weapons system — owner of `ctx.weapons`.
 *
 * Simulation (firing, recoil, spread, ballistics) runs in fixed(dt) at 120 Hz.
 * Viewmodel animation, sway and bob run in update(dt) at frame rate.
 *
 * Every dependency is feature-detected: materials, physics, fx, render and player may
 * all still be stubs. Nothing here throws if they are.
 */

const AX = new THREE.Vector3(1, 0, 0);
const AY = new THREE.Vector3(0, 1, 0);
const AZ = new THREE.Vector3(0, 0, 1);
const CURL = -1; // rotating a finger bone about +Z by a negative angle curls it

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _m = new THREE.Matrix4();

export function createWeaponSystem() {
  return new WeaponSystem();
}

class WeaponSystem {
  constructor() {
    this.name = 'weapons';
    this.priority = 60;
    this.vm = null;
    this.materials = null;
    this.ballistics = null;
    this.instances = new Map();
    this.owned = [];
    this.recoil = new RecoilState();
    this.spread = new SpreadState();
    this.seed = 0xC0DE1;
    this.shotCounter = 0;
    this.fireTimer = 0;
    this.burstLeft = 0;
    this.burstCool = 0;
    this.cycleT = 10;
    this.triggerFinger = 0;
    this.triggerHeld = false;
    this.pendingSwitch = null;
    this.lastState = { speed: 0, sprinting: false, grounded: true, crouched: false };
    this._prevYaw = 0;
    this._prevPitch = 0;
    this._forcedInput = null;
  }

  /* ------------------------------------------------------------------ */

  init(ctx) {
    this.ctx = ctx;
    this.materials = new GunMaterialLibrary(ctx);
    this.materials.installEnvironment(ctx.renderer, ctx.viewScene);
    this.ballistics = new Ballistics(ctx);

    this.vm = new Viewmodel(ctx);
    ctx.viewScene.add(this.vm.root);
    this._installLights(ctx);

    const materialFor = (key) => this.materials.get(key);
    for (const name of Object.keys(DEFS)) {
      try {
        const { def, meta, built } = buildWeapon(name, materialFor, this.seed);
        const inst = new WeaponInstance(name, def, built, meta);
        this.instances.set(name, inst);
        this.vm.addWeapon(inst);
      } catch (err) {
        console.error(`[weapons] failed to build "${name}":`, err);
      }
    }

    for (const n of ['rifle', 'smg', 'shotgun', 'pistol']) if (this.instances.has(n)) this.owned.push(n);

    /* ---- frozen public API ---- */
    const self = this;
    const api = {
      get current() { return self.vm.current; },
      ads: 0,
      /**
       * Cone HALF-ANGLE in radians. This is the same quantity the ballistics
       * solver perturbs each pellet by, so the crosshair the UI draws from
       * `tan(spread) * pxPerRad` encloses exactly the cone the bullets go into.
       */
      spread: 0,
      viewmodelRoot: this.vm.root,
      give: (name) => this.give(name),
      equip: (slot) => this.equip(slot),
      fire: () => this.tryFire(true),
      reload: () => this.startReload(),
      // extensions (never removed, never renamed above)
      list: () => [...this.instances.keys()],
      setFireMode: (m) => this.setFireMode(m),
      melee: () => this.startMelee(),
      inspect: () => this.startInspect(),
      ammo: () => this.ammoState(),
      /** Descriptor for the equipped weapon: { name, displayName, class, suppressed, fireMode, magSize }. */
      info: () => (self.vm.current ? self.vm.current.info() : null),
      /** Fit / remove a suppressor. Published on every subsequent weapon event. */
      setSuppressed: (on, name) => {
        const inst = name ? self.instances.get(name) : self.vm.current;
        if (!inst) return false;
        inst.suppressed = !!on;
        return true;
      },
      system: this,
    };
    ctx.weapons = api;
    this.api = api;

    this.equipByName('rifle');
    this._emitAmmo();
    this._registerShots(ctx);

    ctx.events?.on('player:land', (e) => this.vm.land(e?.impact ?? 0.5));
  }

  /**
   * Viewmodel lighting.
   *
   * The world is lit in real photometric units and the post pipeline auto-exposes for
   * it, so a viewmodel lit with hand-picked intensities either vanishes or blows out
   * the moment the time of day moves. Instead we mirror the world: the key light takes
   * the sun's direction, colour and magnitude, the sky and bounce fills track theirs,
   * and only a small camera-relative fill is ours — the standard trick that keeps the
   * gun readable when the shooter is facing away from the sun.
   */
  _installLights(ctx) {
    if (ctx.viewScene.userData.weaponLights) return;
    ctx.viewScene.userData.weaponLights = true;

    const mk = (parent, color, intensity) => {
      const l = new THREE.DirectionalLight(color, intensity);
      l.castShadow = false;
      parent.add(l);
      parent.add(l.target);
      return l;
    };

    // world-aligned trio, living directly in the view scene
    this.sunKey = mk(ctx.viewScene, 0xfff2e2, 2.2);
    this.skyKey = mk(ctx.viewScene, 0x9fc4ee, 0.6);
    this.bounceKey = mk(ctx.viewScene, 0xb59a76, 0.25);
    this.sunKey.position.set(0.4, 0.8, 0.4);
    this.skyKey.position.set(0.1, 1, 0.1);
    this.bounceKey.position.set(-0.2, -1, -0.2);

    // camera-relative readability fill, so the gun never goes to pure silhouette
    const rig = new THREE.Group();
    rig.name = 'viewmodelFillRig';
    this.vm.root.add(rig);
    this.fillA = mk(rig, 0xdfe8f4, 0.35);
    this.fillA.position.set(-0.55, 0.62, 0.55);
    this.fillB = mk(rig, 0xc8d4e2, 0.16);
    this.fillB.position.set(0.75, 0.10, 0.35);
    this.lightRig = rig;

    this._lightRefresh = 0;
    this._syncWorldLights(ctx, true);
    ctx.events?.on('sky:changed', () => { this._lightRefresh = 0; });
  }

  /** Copy the world's key/sky/bounce magnitudes onto the viewmodel rig. */
  _syncWorldLights(ctx, force = false) {
    if (!force && --this._lightRefresh > 0) return;
    this._lightRefresh = 20;

    let sun = ctx.lighting?.sun ?? null;
    let sky = ctx.lighting?.skyFill ?? null;
    let bounce = ctx.lighting?.bounce ?? null;
    if (!sun) {
      ctx.scene.traverse((o) => {
        if (o.isDirectionalLight && (!sun || o.intensity > sun.intensity)) sun = o;
      });
    }
    if (!sun) return;

    const dir = ctx.sky?.sunDirection
      ?? _v.copy(sun.position).sub(sun.target ? sun.target.position : _v2.set(0, 0, 0)).normalize();
    // A carried weapon lives in the shooter's own shadow: it never sees the full
    // solar term, and a metal at roughness 0.58 under an unattenuated sun produces a
    // specular lobe that clips to white across the whole receiver. SHADE is that
    // occlusion factor; the sky and bounce terms come through almost unattenuated
    // because the gun does see most of the sky hemisphere.
    const SHADE = 0.78;
    const skyI = sky ? sky.intensity : sun.intensity * 0.16;
    this.sunKey.position.copy(dir).normalize().multiplyScalar(10);
    this.sunKey.color.copy(sun.color);
    this.sunKey.intensity = Math.min(sun.intensity * SHADE, skyI * 26.0);

    this.skyKey.intensity = skyI * 3.40;
    if (sky) this.skyKey.color.copy(sky.color);
    const bI = bounce ? bounce.intensity : sun.intensity * 0.06;
    this.bounceKey.intensity = bI * 3.00;
    if (bounce) this.bounceKey.color.copy(bounce.color);

    // the readability fill is a fraction of the key, so it scales with time of day
    this.fillA.intensity = this.sunKey.intensity * 0.18 + skyI * 1.30;
    this.fillB.intensity = this.sunKey.intensity * 0.08 + skyI * 0.55;

    /*
     * Share the world's IBL, at the world's strength.
     *
     * The 1.60x boost that used to live here was one half of why the whole weapon
     * read as flat grey: every structural material was authored as bare metal, so
     * its entire appearance was this environment, and we were then over-driving it.
     * `ProbeRig.syncViewmodelLights` owns the final word on this pair anyway
     * (CONTRACTS.md :: "The viewmodel is on the same scale"); we only seed it so a
     * frame captured before the render system's first update is not black.
     */
    if (ctx.scene.environment && !ctx.viewScene.environment) {
      ctx.viewScene.environment = ctx.scene.environment;
      ctx.viewScene.environmentIntensity = (ctx.scene.environmentIntensity ?? 1) * 0.85;
    }
    this.materials.bindEnvironment(ctx.viewScene);
  }

  /* ------------------------------------------------------------------ *
   * Inventory
   * ------------------------------------------------------------------ */

  give(name) {
    if (!this.instances.has(name)) return null;
    if (!this.owned.includes(name)) this.owned.push(name);
    return this.instances.get(name);
  }

  equip(slot) {
    if (typeof slot === 'string') return this.equipByName(slot);
    const name = Object.keys(DEFS).find((n) => DEFS[n].slot === slot);
    return name ? this.equipByName(name) : null;
  }

  equipByName(name, instant = false) {
    const inst = this.instances.get(name);
    if (!inst || this.vm.current === inst) return this.vm.current;
    // The UI prints `to.displayName`, colours by `to.class`, sizes the ammo
    // counter from `to.magSize` and labels the fire selector from `to.fireMode`.
    // Both descriptors stringify to the internal weapon name, so anything still
    // written against the old string payload is unaffected.
    const from = this.vm.current ? this.vm.current.info() : null;
    const doSwap = () => {
      this.vm.equip(name);
      this.recoil.reset();
      this.spread.reset();
      this.cycleT = 10;
      this.ctx.events?.emit('weapon:switch', { from, to: inst.info() });
      this._emitAmmo();
      this.vm.play(drawSequence(inst));
    };
    if (from && !instant) {
      this.vm.play(holsterSequence(this.vm.current, doSwap));
    } else {
      doSwap();
    }
    return inst;
  }

  nextWeapon() {
    if (!this.owned.length) return;
    const i = this.owned.indexOf(this.vm.current?.name);
    this.equipByName(this.owned[(i + 1) % this.owned.length]);
  }

  setFireMode(mode) {
    const inst = this.vm.current;
    if (!inst) return;
    const i = inst.def.fireModes.indexOf(mode);
    if (i >= 0) inst.fireModeIndex = i;
  }

  cycleFireMode() {
    const inst = this.vm.current;
    if (!inst) return;
    inst.fireModeIndex = (inst.fireModeIndex + 1) % inst.def.fireModes.length;
  }

  ammoState() {
    const i = this.vm.current;
    return i ? { mag: i.mag, reserve: i.reserve, max: i.magSize } : { mag: 0, reserve: 0, max: 0 };
  }

  _emitAmmo() {
    const a = this.ammoState();
    this.ctx.events?.emit('weapon:ammo', a);
  }

  /* ------------------------------------------------------------------ *
   * Firing
   * ------------------------------------------------------------------ */

  get canAct() {
    const s = this.vm.seq?.name;
    return !s || s === 'draw';
  }

  tryFire(explicit = false) {
    const inst = this.vm.current;
    if (!inst) return false;
    if (this.vm.busy && this.vm.seq.name !== 'draw') return false;
    if (this.fireTimer > 0) return false;
    if (inst.mag <= 0) {
      if (explicit) this.startReload();
      return false;
    }
    this.fire(inst);
    return true;
  }

  fire(inst) {
    const ctx = this.ctx;
    const def = inst.def;
    inst.mag--;
    this.fireTimer = 60 / (def.rpm ?? 600);
    this.cycleT = 0;
    this.shotCounter++;

    const imp = this.recoil.fire(def.recoil, this.seed);
    this.spread.fire(def.spread);
    this.vm.fireKick(inst, imp);

    /* ---- world-space muzzle + eject frames ---- */
    this.vm.root.updateMatrixWorld(true);
    const wm = inst.group.matrixWorld;
    const muzzleWorld = _v.copy(inst.meta.muzzle).applyMatrix4(wm);
    const cam = ctx.camera;
    const origin = cam.getWorldPosition(new THREE.Vector3());
    const dir = cam.getWorldDirection(new THREE.Vector3());

    // `spread.value` IS the cone half-angle in radians — the same number that is
    // published on ctx.weapons.spread and that the UI feeds straight into
    // tan(spread) * pxPerRad for the crosshair gap. It is not halved again here;
    // doing so used to make the bullets land inside half the cone the crosshair
    // was drawing.
    const half = this.spread.value;
    const pellets = def.pellets ?? 1;
    for (let p = 0; p < pellets; p++) {
      const d = Ballistics.applySpread(dir, half, this.seed ^ (p * 7919), this.shotCounter, new THREE.Vector3());
      if (def.ballistics.projectile) {
        this.ballistics.spawnProjectile(muzzleWorld.clone(), d, def.ballistics, this.seed, this.shotCounter);
      } else {
        this.ballistics.hitscan(origin, d, def.ballistics, this.seed, this.shotCounter * 31 + p);
      }
    }

    // `dir` is the bore axis at the instant of the shot, world space, unit
    // length — audio places the supersonic crack along it. `weapon` carries
    // `.class` and `.suppressed` so audio can pick its bank and variant without
    // a name lookup table.
    ctx.events?.emit('weapon:fire', {
      weapon: inst.info(),
      weaponName: inst.name,
      origin: muzzleWorld.clone(),
      dir: dir.clone().normalize(),
      seed: this.seed ^ this.shotCounter,
      suppressed: !!inst.suppressed,
      shotIndex: this.shotCounter,
    });
    this._emitAmmo();

    /* ---- FX hooks (all optional) ---- */
    const fx = ctx.fx;
    if (fx) {
      const mFlash = _m.copy(wm).multiply(
        new THREE.Matrix4().makeTranslation(inst.meta.muzzle.x, inst.meta.muzzle.y, inst.meta.muzzle.z));
      try { fx.muzzleFlash?.(mFlash.clone(), { weapon: inst.name, viewmodel: true }); } catch { /* stub */ }
      const ep = inst.meta.ejectPort;
      const mShell = _m.copy(wm).multiply(new THREE.Matrix4().makeTranslation(ep.x, ep.y, ep.z));
      try {
        fx.shell?.(mShell.clone(), {
          weapon: inst.name,
          velocity: inst.meta.ejectDir ? inst.meta.ejectDir.clone().multiplyScalar(3.2) : undefined,
        });
      } catch { /* stub */ }
    }
    try { ctx.render?.shake?.((def.recoil.vertical ?? 0.006) * 22, 0.09); } catch { /* stub */ }
    try { ctx.audio?.gunshot?.(inst.name); } catch { /* stub */ }

    if (def.fireModes[inst.fireModeIndex] === 'pump' && inst.mag >= 0) {
      this.pendingPump = 0.10;
    }
    return imp;
  }

  startReload() {
    const inst = this.vm.current;
    if (!inst || this.vm.busy) return false;
    if (inst.mag >= inst.magSize || inst.reserve <= 0) return false;

    if (inst.def.class === 'shotgun') {
      const want = Math.min(inst.magSize - inst.mag, inst.reserve);
      const seq = shellLoadSequence(inst, want, this.ctx.events);
      const base = seq.onEnd;
      seq.onEnd = (vm) => {
        inst.reserve -= want; inst.mag += want; this._emitAmmo(); base?.(vm);
      };
      this.vm.play(seq);
      return true;
    }

    const empty = inst.mag === 0;
    const seq = reloadSequence(inst, empty, this.ctx.events);
    const base = seq.onEnd;
    seq.onEnd = (vm) => {
      const need = inst.magSize - inst.mag;
      const take = Math.min(need, inst.reserve);
      inst.mag += take; inst.reserve -= take;
      this._emitAmmo();
      base?.(vm);
    };
    this.vm.play(seq);
    return true;
  }

  startMelee() {
    if (!this.vm.current || this.vm.busy) return false;
    this.vm.play(meleeSequence(this.vm.current, this.ctx.events));
    return true;
  }

  startInspect() {
    if (!this.vm.current || this.vm.busy) return false;
    this.vm.play(inspectSequence(this.vm.current));
    return true;
  }

  /* ------------------------------------------------------------------ *
   * Simulation
   * ------------------------------------------------------------------ */

  fixed(dt, ctx) {
    const inst = this.vm.current;
    if (!inst) return;
    const input = this._forcedInput ?? ctx.input?.state ?? null;
    const player = ctx.player ?? {};

    if (this.fireTimer > 0) this.fireTimer -= dt;
    if (this.burstCool > 0) this.burstCool -= dt;

    /* ---- input -> intent ---- */
    const lock = this.lock;
    const sprinting = lock ? lock.sprint > 0.5
      : !!(player.sprinting ?? (input?.sprint && (player.speed ?? 0) > 3.0));
    if (lock) {
      this.vm.adsTarget = lock.ads; this.vm.ads = lock.ads;
      this.vm.sprintTarget = lock.sprint; this.vm.sprint = lock.sprint;
      this.vm.tacSprint = lock.tac;
    } else {
      const wantAds = !!(input?.ads) && !sprinting
        && !(this.vm.busy && this.vm.seq.name?.startsWith('reload'));
      this.vm.adsTarget = wantAds ? 1 : 0;
      this.vm.sprintTarget = sprinting && !wantAds ? 1 : 0;
    }

    if (input && !lock) {
      const mode = inst.fireMode;
      const held = !!input.fire;
      const edge = !!input.fireEdge;
      if (!held && this.triggerHeld) {
        this.recoil.shotIndex = 0; // pattern restarts on trigger release, as in COD
        this.burstLeft = 0;
      }
      this.triggerHeld = held;

      if (mode === 'auto') {
        if (held && !sprinting) this.tryFire();
      } else if (mode === 'semi' || mode === 'pump') {
        if (edge && !sprinting) this.tryFire();
      } else if (mode === 'burst') {
        if (edge && this.burstLeft === 0 && this.burstCool <= 0 && !sprinting) {
          this.burstLeft = inst.def.burstCount ?? 3;
        }
        if (this.burstLeft > 0 && this.fireTimer <= 0) {
          if (this.tryFire()) {
            this.burstLeft--;
            if (this.burstLeft === 0) this.burstCool = inst.def.burstGap ?? 0.24;
          } else this.burstLeft = 0;
        }
      }

      if (input.reload) this.startReload();
      if (input.meleeEdge) this.startMelee();
      if (input.slot) this.equip(input.slot);
      if (input.nextWeapon) this.nextWeapon();
    }

    /* ---- pump action after a shotgun shot ---- */
    if (this.pendingPump !== undefined) {
      this.pendingPump -= dt;
      if (this.pendingPump <= 0) {
        this.pendingPump = undefined;
        if (!this.vm.busy && inst.def.class === 'shotgun' && inst.mag > 0) this.vm.play(pumpSequence(inst));
      }
    }

    /* ---- recoil -> camera ---- */
    const d = this.recoil.step(dt, inst.def.recoil);
    if (typeof player.addRecoil === 'function' && (d.dPitch || d.dYaw)) {
      try { player.addRecoil(d.dPitch, d.dYaw); } catch { /* stub */ }
    }

    /* ---- spread ---- */
    const sp = this.spread.step(dt, inst.def.spread, {
      ads: this.vm.ads,
      speed: player.speed ?? 0,
      crouched: !!player.crouched,
      grounded: player.grounded !== false,
      sprinting,
    });
    this.api.spread = sp;
    this.api.ads = this.vm.ads;

    this.ballistics.stepProjectiles(dt);
  }

  /* ------------------------------------------------------------------ *
   * Presentation
   * ------------------------------------------------------------------ */

  update(dt, ctx) {
    const inst = this.vm.current;
    if (!inst) return;
    const player = ctx.player ?? {};
    const input = this._forcedInput ?? ctx.input?.state ?? null;

    /* ---- reset the bind pose, then layer cycling on top ---- */
    inst.resetPose();
    this._applyCycle(dt, inst);

    /* ---- gather motion state ---- */
    let dx = 0, dy = 0;
    if (player.yaw !== undefined) {
      dx = player.yaw - this._prevYaw;
      dy = player.pitch - this._prevPitch;
      if (dx > Math.PI) dx -= Math.PI * 2; else if (dx < -Math.PI) dx += Math.PI * 2;
      this._prevYaw = player.yaw; this._prevPitch = player.pitch;
      // a teleport or respawn is not a mouse movement
      if (Math.abs(dx) > 0.45 || Math.abs(dy) > 0.45) { dx = 0; dy = 0; }
      dx = -dx; dy = -dy;
    } else if (input) {
      const sens = ctx.input?.sensitivity ?? 0.0022;
      dx = -(input.dx ?? 0) * sens;
      dy = -(input.dy ?? 0) * sens;
    }

    const state = {
      speed: player.speed ?? 0,
      sprinting: !!player.sprinting,
      tacSprint: !!(player.tacSprint ?? (player.sprinting && (player.speed ?? 0) > 6.2)),
      grounded: player.grounded !== false,
      crouched: !!player.crouched,
      lookDx: dx, lookDy: dy,
      bob: player.bobOffset instanceof THREE.Vector3 ? player.bobOffset : null,
      sway: player.viewSway ?? null,
    };

    this.vm.update(dt, state);
    this.api.ads = this.vm.ads;

    this._syncWorldLights(ctx);

    /* ---- keep the rig glued to the view camera ---- */
    ctx.viewCamera.updateMatrixWorld(true);
    this.vm.syncToCamera(ctx.viewCamera);
    if (Math.abs(ctx.viewCamera.fov - this.vm.fov) > 1e-3) {
      ctx.viewCamera.fov = this.vm.fov;
      ctx.viewCamera.updateProjectionMatrix();
    }
  }

  /** Bolt / dust cover / trigger finger, driven off the last shot. */
  _applyCycle(dt, inst) {
    const def = inst.def;
    this.cycleT += dt;
    const T = def.cycleTime ?? 0.055;
    const rig = def.rig ?? {};
    let s = 0;
    if (this.cycleT < T) {
      const u = this.cycleT / T;
      s = u < 0.42 ? smoothstep(u / 0.42) : 1 - smoothstep((u - 0.42) / 0.58);
    }
    // bolt held open on an empty magazine
    const locked = inst.mag === 0 && def.class !== 'shotgun' && def.rig?.bolt ? 1 : 0;
    const boltS = Math.max(s, locked);
    if (rig.bolt) inst.translateBone(rig.bolt.bone, 0, 0, rig.bolt.travel * boltS);
    if (rig.dust) inst.rotateBone(rig.dust.bone, AZ, rig.dust.angle * Math.max(boltS, s));
    if (rig.charging && def.class === 'pistol') {
      inst.translateBone(rig.charging.bone, 0, 0, (rig.charging.travel ?? 0) * boltS);
    }

    /* ---- trigger blade + trigger finger ---- */
    const want = (this.triggerHeld || this.cycleT < T * 1.6) && !this.vm.busy ? 1 : 0;
    this.triggerFinger = lerp(this.triggerFinger, want, 1 - Math.exp(-26 * dt));
    const tf = this.triggerFinger;
    if (rig.trigger) inst.rotateBone(rig.trigger.bone, AX, rig.trigger.angle * tf);
    /*
     * The bind pose already IS the rest pose — `Hands.buildHand` bakes a fraction
     * of the solved curl into it — so the runtime only has to blend in the
     * remainder. `meta.triggerCurl` is solved per weapon against that weapon's
     * actual blade anchor; the hard-coded [0.62, 0.72, 0.46] it replaces left the
     * fingertip 35-92 mm short of the trigger on all four guns.
     */
    const curl = inst.meta.triggerCurl || [0, 0, 0];
    for (let j = 0; j < 3; j++) {
      inst.rotateBone(`right_index${j}`, AZ, CURL * (curl[j] * tf));
    }
  }

  resize() { /* the view camera aspect is owned by the engine */ }

  /* ------------------------------------------------------------------ *
   * Capture shots
   * ------------------------------------------------------------------ */

  _registerShots(ctx) {
    const sys = this;

    /**
     * Put the player where their own `player_stand` shot puts them, so the
     * viewmodel is framed against the level exactly as it is in play. Falls back
     * to a fixed camera while the player module is a stub.
     */
    const stage = (c, yawOffset = 0, pitch = -0.045) => {
      const P = c.get?.('player');
      if (P && typeof P.place === 'function' && typeof P.run === 'function') {
        try {
          const feet = P.movement.spawnFeet(new THREE.Vector3());
          P.place(feet, P._spawnYaw() + yawOffset, pitch);
          P.run(0.55);
          P.freeze(true);
        } catch (e) {
          look(c, [1.6, 1.72, 6.2], [0.4, 1.35, -3.0], 80);
        }
      } else {
        look(c, [1.6, 1.72, 6.2], [0.4, 1.35, -3.0], 80);
      }
      sys.lock = { ads: 0, sprint: 0, tac: 0 };
      sys._forcedInput = null;
      // adopt the player's look angles so the first frame after a teleport does not
      // register a full-turn mouse delta and fling the sway spring
      sys._prevYaw = c.player?.yaw ?? 0;
      sys._prevPitch = c.player?.pitch ?? 0;
      // Restore per-mesh visibility (the turntable shots hide the hands), but keep
      // exactly one weapon GROUP on: `equipByName` early-returns when the weapon is
      // already current, so on a rifle shot nothing downstream would have corrected
      // a blanket un-hide, and every other gun would render on top of it.
      for (const inst of sys.instances.values()) {
        for (const m of inst.meshes) m.visible = true;
        inst.group.visible = (inst === sys.vm.current);
      }
      sys.vm.rigOverride = null;
      sys.vm.stopSequence();
      sys.recoil.reset();
      sys.spread.reset();
      sys.vm.swayPos.reset(); sys.vm.swayRot.reset();
      sys.vm.recoilPos.reset(); sys.vm.recoilRot.reset();
      sys.vm.bobPos.set(0, 0, 0); sys.vm.bobRot.set(0, 0, 0);
      sys.vm.jumpSpring.set(0);
      sys.vm.breathT = 0; sys.vm.bobPhase = 0;
      sys.vm.lagSmooth.set(0, 0);
      sys.vm.lower = 0;
      sys.triggerFinger = 0;
      sys.triggerHeld = false;
      sys.cycleT = 10;
      sys.vm.fovBase = 60;
      sys.vm.fov = 60;
    };

    const settleTo = (target, sprint = 0, tac = 0) => {
      // equipping queues a draw sequence; a still frame must not catch it mid-raise
      sys.vm.stopSequence();
      sys.lock = { ads: target, sprint, tac };
      sys.vm.adsTarget = target; sys.vm.ads = target;
      sys.vm.sprintTarget = sprint; sys.vm.sprint = sprint;
      sys.vm.tacSprint = tac;
      if (target > 0.5 && sys.vm.current) {
        sys.vm.fov = sys.vm.current.def.adsFov;
        sys.ctx.viewCamera.fov = sys.vm.fov;
        sys.ctx.viewCamera.updateProjectionMatrix();
      }
    };

    registerShot('wpn_rifle_hip', (c) => {
      stage(c);
      sys.equipByName('rifle', true);
      settleTo(0);
    }, { settle: 14, desc: 'assault rifle, hip-fire pose, full first-person framing' });

    registerShot('wpn_rifle_ads', (c) => {
      stage(c);
      sys.equipByName('rifle', true);
      settleTo(1);
    }, { settle: 14, desc: 'aiming down the red dot, reticle dead centre' });

    registerShot('wpn_closeup', (c) => {
      stage(c);
      sys.equipByName('rifle', true);
      settleTo(0);
      sys.cycleT = 0.020;   // bolt mid-travel, dust cover open
      sys.triggerFinger = 1;
      sys.vm.rigOverride = camIn([0.152, 0.062, 0.052], [0.014, 0.006, -0.084], 34);
    }, { settle: 6, desc: 'macro on the receiver and ejection port' });

    registerShot('wpn_hands', (c) => {
      stage(c);
      sys.equipByName('rifle', true);
      settleTo(0);
      /*
       * RE-FRAMED (iteration 4). The reviewer's pixel coordinates for this
       * shot no longer apply.
       *
       * The previous camera sat at z = -0.290, i.e. 3 cm FORWARD of the support
       * grip at z = -0.322, and looked rearward along the bore. The support
       * thumb is a C-clamp laid along the handguard pointing at the muzzle — so
       * it pointed straight at that camera and foreshortened to nothing, and
       * the support hand itself sat at the very edge of frame behind its own
       * forearm. The shot could not show the thing it is named for; the
       * previous pass chose not to move a camera the reviewer cites by
       * coordinate, and that decision is overridden here.
       *
       * The camera is now roughly PERPENDICULAR to the bore, on the left flank,
       * 27 degrees above it and between the two hands. The support thumb runs
       * across the frame instead of into it, the C-clamp reads, and the firing
       * hand — grip, trigger finger, thumb — is in the same shot rather than
       * behind the receiver.
       */
      sys.vm.rigOverride = camIn([-0.330, 0.158, -0.268], [0.016, -0.046, -0.214], 48);
    }, { settle: 6, desc: 'gloved hands on the grip and handguard' });

    registerShot('wpn_reload', (c) => {
      stage(c);
      sys.equipByName('rifle', true);
      settleTo(0);
      const inst = sys.vm.current;
      inst.mag = 0;
      sys.vm.play(reloadSequence(inst, true, null));
      sys.vm.seqT = 0.60;   // magazine in flight
      sys.vm.update(1 / 240, { speed: 0, lookDx: 0, lookDy: 0 });
    }, { settle: 1, desc: 'mid-reload, magazine in flight' });

    registerShot('wpn_sprint', (c) => {
      stage(c);
      sys.equipByName('rifle', true);
      settleTo(0, 1, 0);
    }, { settle: 14, desc: 'sprint pose' });

    registerShot('wpn_tacsprint', (c) => {
      stage(c);
      sys.equipByName('rifle', true);
      settleTo(0, 1, 1);
    }, { settle: 14, desc: 'tactical sprint pose' });

    registerShot('wpn_inspect', (c) => {
      stage(c);
      sys.equipByName('rifle', true);
      settleTo(0);
      sys.vm.play(inspectSequence(sys.vm.current));
      sys.vm.seqT = 1.05;
    }, { settle: 1, desc: 'inspect animation, left side presented' });

    for (const n of ['shotgun', 'pistol', 'smg']) {
      registerShot(`wpn_${n}`, (c) => {
        stage(c);
        sys.equipByName(n, true);
        settleTo(0);
      }, { settle: 14, desc: `${DEFS[n].label} in the hip pose` });
      registerShot(`wpn_${n}_ads`, (c) => {
        stage(c);
        sys.equipByName(n, true);
        settleTo(1);
      }, { settle: 14, desc: `${DEFS[n].label} aiming down the sights` });
    }

    /* ---- turntable diagnostics: the weapon alone, in weapon space ---- */
    const HAND_MESHES = ['wpn_glove', 'wpn_sleeve', 'wpn_webbing'];
    const showHands = (on) => {
      for (const inst of sys.instances.values()) {
        for (const m of inst.meshes) if (HAND_MESHES.includes(m.name)) m.visible = on;
      }
    };
    const turntable = (name, weapon, from, at, fov, hands) => {
      registerShot(name, (c) => {
        stage(c);
        sys.equipByName(weapon, true);
        settleTo(0);
        showHands(hands);
        sys.vm.rigOverride = camIn(from, at, fov);
      }, { settle: 6, desc: `${weapon} diagnostic view` });
    };
    turntable('wpn_dbg_side', 'rifle', [1.60, 0.02, -0.150], [0, 0.02, -0.150], 17, false);
    turntable('wpn_dbg_side_hands', 'rifle', [1.60, 0.02, -0.150], [0, 0.02, -0.150], 17, true);
    turntable('wpn_dbg_iso', 'rifle', [0.72, 0.40, 0.55], [0, -0.02, -0.16], 25, true);
    turntable('wpn_dbg_grip', 'rifle', [0.40, -0.02, 0.30], [0.0, -0.055, -0.005], 22, true);
    turntable('wpn_dbg_gripbare', 'rifle', [0.55, -0.055, 0.28], [0.0, -0.075, 0.030], 14, false);
    turntable('wpn_dbg_stock', 'rifle', [0.60, 0.02, 0.30], [0.0, -0.010, 0.130], 22, false);
    turntable('wpn_dbg_smg', 'smg', [1.40, 0.02, -0.130], [0, 0.02, -0.130], 17, false);
    turntable('wpn_dbg_shotgun', 'shotgun', [2.10, 0.02, -0.230], [0, 0.02, -0.230], 17, false);
    turntable('wpn_dbg_pistol', 'pistol', [0.62, -0.02, -0.060], [0, -0.02, -0.060], 17, false);

    /** Camera placed in weapon space; the rig transform is its inverse. */
    function camIn(from, at, fov) {
      const m = new THREE.Matrix4();
      const eye = new THREE.Vector3(...from);
      const tgt = new THREE.Vector3(...at);
      m.lookAt(eye, tgt, new THREE.Vector3(0, 1, 0));
      m.setPosition(eye);
      sys.vm.fov = fov;
      return m.invert();
    }

    /* ---- numeric verification hooks used by tools/ ---- */
    ctx.weapons.__debug = {
      /** Deterministic recoil pattern: cumulative (pitch, yaw) for N shots. */
      recoilPattern(weapon = 'rifle', seed = 0xC0DE1, n = 30) {
        const prof = DEFS[weapon].recoil;
        const out = [];
        let p = 0, y = 0;
        for (let i = 0; i < n; i++) {
          const imp = recoilImpulse(prof, i, seed);
          p += imp.pitch; y += imp.yaw;
          out.push([p, y]);
        }
        return out;
      },
      /** Screen-space position of the sight point, in pixels from the centre. */
      reticlePixels() {
        const inst = sys.vm.current;
        if (!inst) return null;
        sys.vm.root.updateMatrixWorld(true);
        const s = inst.meta.sight;
        const p = new THREE.Vector3(0, s.axis, s.z).applyMatrix4(inst.group.matrixWorld);
        p.project(ctx.viewCamera);
        const w = ctx.engine.width, h = ctx.engine.height;
        return { ndc: [p.x, p.y], px: [p.x * w * 0.5, p.y * h * 0.5], ads: sys.vm.ads };
      },
      viewmodelDrawCalls() {
        let n = 0;
        ctx.viewScene.traverse((o) => { if (o.isMesh && o.visible && o.parent?.visible) n++; });
        return n;
      },
      /** What each viewmodel mesh is actually bound to — the flat-grey postmortem. */
      surfaceReport(weapon = 'rifle') {
        const inst = sys.instances.get(weapon);
        if (!inst) return null;
        const out = {};
        for (const m of inst.meshes) {
          const mat = m.material;
          out[m.name] = {
            tris: m.geometry.attributes.position.count / 3,
            map: !!mat.map,
            normalMap: !!mat.normalMap,
            orm: !!mat.userData.roughBand,
            roughBand: mat.userData.roughBand ?? null,
            metalness: mat.metalness,
            envIntensity: mat.envMapIntensity,
          };
        }
        out._env = {
          viewSceneEnv: !!ctx.viewScene.environment,
          sameAsWorld: ctx.viewScene.environment === ctx.scene.environment,
          intensity: ctx.viewScene.environmentIntensity,
        };
        return out;
      },
      /** Mean of each baked vertex channel, per mesh. */
      channelReport(weapon = 'rifle') {
        const inst = sys.instances.get(weapon);
        const out = {};
        for (const m of inst.meshes) {
          const row = {};
          for (const k of ['aWear', 'aGrime', 'aAO', 'aSky']) {
            const a = m.geometry.getAttribute(k);
            if (!a) continue;
            let sum = 0, mn = 1, mx = 0;
            for (let i = 0; i < a.count; i++) {
              const v = a.array[i];
              sum += v; if (v < mn) mn = v; if (v > mx) mx = v;
            }
            row[k] = [+(sum / a.count).toFixed(3), +mn.toFixed(3), +mx.toFixed(3)];
          }
          out[m.name] = row;
        }
        return out;
      },
      viewmodelTriangles() {
        let n = 0;
        ctx.viewScene.traverse((o) => {
          if (o.isMesh && o.visible && o.parent?.visible) n += o.geometry.attributes.position.count / 3;
        });
        return n;
      },
      /**
       * Numeric grip-contact check: signed clearance from each finger to the grip
       * cylinder, in millimetres. Positive = floating, negative = intersecting.
       * The wrap solver should hold every finger inside a fraction of a millimetre.
       *
       * The RIGHT INDEX is deliberately not in this table. It is the trigger
       * finger: `Hands.js` builds it indexed straight along the frame instead of
       * wrapped around the grip, so its distance to the grip cylinder is ~75 mm by
       * design and measuring it here would be measuring the wrong thing. It is
       * checked separately, against the surface it is actually supposed to touch —
       * see `trigger` below.
       */
      contact(weapon = 'rifle') {
        const inst = sys.instances.get(weapon);
        if (!inst) return null;
        inst.resetPose();
        inst.group.updateMatrixWorld(true);
        const inv = new THREE.Matrix4().copy(inst.group.matrixWorld).invert();
        const out = {};
        // half-thickness of each finger, so the number reported is the clearance
        // between the glove SURFACE and the grip, not between bone centres
        const HALF = { index: 0.0103, middle: 0.0105, ring: 0.0100, pinky: 0.0091, thumb: 0.0115 };
        for (const [side, key] of [['right', 'rightGrip'], ['left', 'leftGrip']]) {
          const a = inst.meta.anchors[key];
          if (!a) continue;
          const rows = [];
          const fingers = side === 'right'
            ? ['middle', 'ring', 'pinky', 'thumb']
            : ['index', 'middle', 'ring', 'pinky', 'thumb'];
          for (const f of fingers) {
            const b = inst.boneByName.get(`${side}_${f}2`);
            if (!b) continue;
            const p = new THREE.Vector3().setFromMatrixPosition(b.matrixWorld).applyMatrix4(inv);
            const d = new THREE.Vector3().subVectors(p, a.point);
            d.addScaledVector(a.dir, -d.dot(a.dir));
            rows.push([f, +((d.length() - a.radius - HALF[f]) * 1000).toFixed(2)]);
          }
          out[side] = Object.fromEntries(rows);
        }
        out.trigger = this.triggerContact(weapon);
        let worst = 0;
        for (const side of ['right', 'left']) {
          for (const v of Object.values(out[side] || {})) worst = Math.max(worst, Math.abs(v));
        }
        out.worstGripMm = +worst.toFixed(3);
        return out;
      },

      /**
       * Trigger-finger contact, in millimetres, measured against the trigger blade
       * rather than the grip. Reported for both poses the finger actually holds:
       * `rest`, indexed along the frame above the guard, and `pressed`, curled onto
       * the blade. `pressed` is the one that must read as contact.
       */
      triggerContact(weapon = 'rifle') {
        const inst = sys.instances.get(weapon);
        const anchors = inst?.meta?.anchors;
        if (!anchors?.trigger) return null;
        const DISTAL = inst.meta.indexDistal ?? 0.0228;   // solved distal phalanx length
        const HALF = 0.0103;        // finger half-thickness at the pad
        const measure = (curls, target) => {
          inst.resetPose();
          for (let j = 0; j < 3; j++) {
            inst.rotateBone(`right_index${j}`, AZ, CURL * curls[j]);
          }
          inst.group.updateMatrixWorld(true);
          const inv = new THREE.Matrix4().copy(inst.group.matrixWorld).invert();
          const b = inst.boneByName.get('right_index2');
          if (!b) return null;
          const m = new THREE.Matrix4().multiplyMatrices(inv, b.matrixWorld);
          const j2 = new THREE.Vector3().setFromMatrixPosition(m);
          const ax = new THREE.Vector3().setFromMatrixColumn(m, 0).normalize();
          // closest point on the distal phalanx segment to the blade anchor
          const t = THREE.MathUtils.clamp(_v.copy(target).sub(j2).dot(ax), 0, DISTAL);
          const p = j2.clone().addScaledVector(ax, t);
          return +((p.distanceTo(target) - HALF) * 1000).toFixed(2);
        };
        // the same curls the runtime applies in _applyCycle
        const curl = inst.meta.triggerCurl || [0, 0, 0];
        const rest = measure([0, 0, 0], anchors.triggerRest ?? anchors.trigger);
        const pressed = measure(curl, anchors.trigger);
        inst.resetPose();
        return { restMm: rest, pressedMm: pressed, curl: curl.map((v) => +v.toFixed(4)) };
      },
      bones(weapon = 'rifle', filter = '') {
        const inst = sys.instances.get(weapon);
        if (!inst) return null;
        inst.resetPose();
        inst.group.updateMatrixWorld(true);
        const inv = new THREE.Matrix4().copy(inst.group.matrixWorld).invert();
        const out = {};
        for (const [n, b] of inst.boneByName) {
          if (filter && !n.includes(filter)) continue;
          const p = new THREE.Vector3().setFromMatrixPosition(b.matrixWorld).applyMatrix4(inv);
          out[n] = [+p.x.toFixed(4), +p.y.toFixed(4), +p.z.toFixed(4)];
        }
        return out;
      },
      wearStats(weapon = 'rifle') {
        const inst = sys.instances.get(weapon);
        const out = {};
        for (const m of inst.meshes) {
          const a = m.geometry.getAttribute('aWear');
          if (!a) continue;
          let sum = 0, mx = 0;
          for (let i = 0; i < a.count; i++) { sum += a.array[i]; if (a.array[i] > mx) mx = a.array[i]; }
          out[m.name] = { mean: +(sum / a.count).toFixed(3), max: +mx.toFixed(3) };
        }
        return out;
      },
      state() {
        return {
          weapon: sys.vm.current?.name, ads: sys.vm.ads, spread: sys.api.spread,
          mag: sys.vm.current?.mag, reserve: sys.vm.current?.reserve,
          seq: sys.vm.seq?.name ?? null,
        };
      },
      /**
       * ADS sight-picture stability.
       *
       * Locks the weapon at ads = 1, walks the viewmodel through `cycles` full bob
       * cycles at `speed` m/s — through BOTH bob paths, the player rig's
       * `bobOffset` vector and the internal phase fallback — and reports how far
       * the sight point drifts from the pixel centre. A COD sight picture does not
       * move at all: the pass mark is < 1 px.
       */
      adsBobStability(cycles = 3, speed = 4.4, hz = 120) {
        const inst = sys.vm.current;
        if (!inst) return null;
        const vm = sys.vm;
        const dt = 1 / hz;
        const saved = { lock: sys.lock, ads: vm.ads, adsTarget: vm.adsTarget };
        const out = {};

        const run = (label, useBobVector) => {
          sys.lock = { ads: 1, sprint: 0, tac: 0 };
          vm.adsTarget = 1; vm.ads = 1;
          vm.swayPos.reset(); vm.swayRot.reset();
          vm.recoilPos.reset(); vm.recoilRot.reset();
          vm.bobPhase = 0; vm.breathT = 0; vm.lagSmooth.set(0, 0);
          vm.jumpSpring.set(0); vm.lower = 0;
          vm.stopSequence();

          const bob = new THREE.Vector3();
          const state = {
            speed, sprinting: false, tacSprint: false, grounded: true, crouched: false,
            lookDx: 0, lookDy: 0, bob: null, sway: null,
          };
          // bob frequency the player rig uses at this speed, radians/second
          const w = 4.6 + Math.min(speed / 4.4, 1.35) * 4.4;
          let t = 0;
          const step = () => {
            t += dt;
            if (useBobVector) {
              const ph = t * w;
              bob.set(Math.sin(ph) * 0.028, Math.sin(ph * 2) * 0.021, Math.cos(ph * 2) * 0.008);
              state.bob = bob;
              state.sway = { x: Math.sin(ph) * 0.004, y: Math.sin(ph * 2) * 0.003 };
            }
            vm.update(dt, state);
            ctx.viewCamera.updateMatrixWorld(true);
            vm.syncToCamera(ctx.viewCamera);
          };
          for (let i = 0; i < hz; i++) step();          // one second to settle
          const n = Math.ceil((cycles * 2 * Math.PI / w) * hz);
          let max = 0, maxAt = 0;
          const px = [];
          for (let i = 0; i < n; i++) {
            step();
            const r = ctx.weapons.__debug.reticlePixels();
            const d = Math.hypot(r.px[0], r.px[1]);
            px.push(+d.toFixed(4));
            if (d > max) { max = d; maxAt = i; }
          }
          out[label] = {
            samples: n, maxPx: +max.toFixed(4), maxAtSample: maxAt,
            meanPx: +(px.reduce((a, b) => a + b, 0) / n).toFixed(4),
            ads: vm.ads, pass: max < 1.0,
          };
        };

        run('bobVector', true);
        run('internalPhase', false);
        sys.lock = saved.lock;
        vm.ads = saved.ads; vm.adsTarget = saved.adsTarget;
        out.pass = out.bobVector.pass && out.internalPhase.pass;
        return out;
      },

      /** Published spread in radians, plus what it means in pixels and metres. */
      spreadReport(weapon = 'rifle') {
        const prof = DEFS[weapon].spread;
        const half = sys.api.spread;
        return {
          published: half,
          unit: 'cone half-angle, radians',
          degrees: +(half * 180 / Math.PI).toFixed(4),
          ballisticsHalfAngle: half,          // Weapons.fire() uses it unscaled
          radiusAt10m: +(Math.tan(half) * 10).toFixed(4),
          profileHipHalfAngle: prof.spreadHip,
          profileAdsHalfAngle: prof.spreadAds,
        };
      },

      /** Payload shapes actually emitted, captured by listening to the bus. */
      eventProbe(weapon = 'rifle') {
        const seen = {};
        const strip = (o) => JSON.parse(JSON.stringify(o, (k, v) => (
          v && v.isVector3 ? [+v.x.toFixed(4), +v.y.toFixed(4), +v.z.toFixed(4)] : v)));
        const offs = [
          ctx.events.on('weapon:fire', (p) => { seen.fire = seen.fire || strip(p); }),
          ctx.events.on('weapon:switch', (p) => { seen.switch = seen.switch || strip(p); }),
          ctx.events.on('weapon:reload', (p) => {
            if (p.phase === 'start') seen.reloadStart = seen.reloadStart || strip(p);
            else seen.reloadEnd = seen.reloadEnd || strip(p);
          }),
        ];
        try {
          const other = weapon === 'rifle' ? 'smg' : 'rifle';
          sys.equipByName(other, true);
          sys.equipByName(weapon, true);
          const inst = sys.vm.current;
          sys.vm.stopSequence();
          sys.fireTimer = 0;
          inst.mag = Math.max(2, inst.mag);
          sys.fire(inst);
          inst.mag = 1;
          inst.reserve = Math.max(inst.reserve, 30);
          sys.vm.stopSequence();
          sys.startReload();
          sys.vm.stopSequence();
        } finally {
          for (const off of offs) off();
        }
        return seen;
      },

      fireBurst(n = 30, weapon = 'rifle') {
        sys.equipByName(weapon, true);
        const inst = sys.vm.current;
        inst.mag = Math.max(inst.mag, n);
        sys.recoil.reset();
        const out = [];
        for (let i = 0; i < n; i++) {
          const imp = sys.recoil.fire(inst.def.recoil, sys.seed);
          out.push([imp.pitch, imp.yaw]);
        }
        sys.recoil.reset();
        return out;
      },
    };
  }

  dispose() {
    this.materials?.dispose();
  }
}
